/* =============================================================
   ROCKPOT CART — AJAX ATC + slide-out drawer
   Replaces the old toast-notification behavior.
   - Intercepts all add-to-cart requests (fetch, XHR, and form submits)
   - On success: opens the slide-out drawer with fresh cart contents
   - Handles quantity adjust, remove, and upsell "+ ADD"
   - Drawer titles + upsell products are configurable in Shopify customizer
     (see sections/rockpot-cart-drawer.liquid schema)
   ============================================================= */
(function () {
  'use strict';

  // ---- Config ----
  var BADGE_SELECTOR = '.nav-cart-count';
  var CART_LINK_SELECTOR = '.nav-cart-link';

  // ---- State ----
  var drawer = null;
  var overlay = null;
  var titleEl = null;
  var titleDefault = 'Your Cart';
  var titleAdded = 'Added to Cart';
  var isRefreshing = false;
  var nativeFetch = window.fetch.bind(window);

  // =========================================================
  // Utilities
  // =========================================================

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // =========================================================
  // Badge update
  // =========================================================

  function updateBadge(count) {
    $$(BADGE_SELECTOR).forEach(function (badge) {
      badge.textContent = count;
      badge.setAttribute('data-cart-count', count);
      badge.style.display = count > 0 ? '' : 'none';
    });

    $$(CART_LINK_SELECTOR).forEach(function (link) {
      link.classList.add('cart-bump');
      setTimeout(function () { link.classList.remove('cart-bump'); }, 600);
    });
  }

  // =========================================================
  // Drawer — open / close
  // =========================================================

  function ensureDrawer() {
    if (!drawer) {
      drawer = document.getElementById('rockpot-drawer');
      overlay = document.getElementById('rockpot-drawer-overlay');
      if (drawer) {
        titleEl = $('[data-drawer-title]', drawer);
        titleDefault = drawer.getAttribute('data-title-default') || titleDefault;
        titleAdded = drawer.getAttribute('data-title-added') || titleAdded;
      }
    }
    return drawer;
  }

  function openDrawer(wasAfterAdd) {
    if (!ensureDrawer()) return;
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rockpot-drawer-open');
    if (titleEl) {
      titleEl.textContent = wasAfterAdd ? titleAdded : titleDefault;
    }
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rockpot-drawer-open');
  }

  // =========================================================
  // Render cart into drawer
  // =========================================================

  function renderCart(cart) {
    if (!ensureDrawer()) return;

    var itemsEl = $('[data-cart-items]', drawer);
    var emptyEl = $('[data-cart-empty]', drawer);
    var totalEl = $('[data-cart-total]', drawer);
    var footerEl = $('[data-cart-footer]', drawer);
    var upsellEl = $('[data-upsell]', drawer);

    var isEmpty = !cart || cart.item_count === 0;

    drawer.setAttribute('data-cart-empty', isEmpty ? 'true' : 'false');
    if (emptyEl) emptyEl.hidden = !isEmpty;
    if (footerEl) footerEl.style.display = isEmpty ? 'none' : '';

    if (itemsEl) {
      if (isEmpty) {
        itemsEl.innerHTML = '';
      } else {
        itemsEl.innerHTML = cart.items.map(renderItem).join('');
      }
    }

    if (totalEl) totalEl.textContent = formatMoney(cart ? cart.total_price : 0);

    // Gift notice: only when a 7QT or Mini bundle (the gift-eligible products) is in the cart
    var giftNote = $('[data-gift-note]', drawer);
    if (giftNote) giftNote.hidden = isEmpty || !cart.items.some(function (i) { return i.handle === 'rockpot-full-kit' || i.handle === 'rockpot-mini-bundle'; });

    if (upsellEl) {
      var rawHandles = upsellEl.getAttribute('data-upsell-handles') || '';
      var handles = rawHandles.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      if (!isEmpty && handles.length) {
        upsellEl.hidden = false;
        loadUpsellProducts(upsellEl, handles, cart);
      } else {
        upsellEl.hidden = true;
      }
    }
  }

  function renderItem(item) {
    var image = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">'
      : '';

    // Show variant_title only if it's real variant info (not a default and not a price string).
    // Some apps/configs stuff prices into variant_title which looks wrong in the drawer.
    var variantLine = '';
    var variantTitle = item.variant_title || '';
    var looksLikePrice = /^\s*\$?\s*[\d,.]+\s*$/.test(variantTitle);
    if (variantTitle && variantTitle !== 'Default Title' && !looksLikePrice) {
      variantLine = '<div class="rockpot-drawer__item-variant">' + escapeHtml(variantTitle) + '</div>';
    }

    // Price: when discounted (final < original), show strikethrough original + bold sale price
    var isDiscounted = item.final_price < item.original_price;
    var priceHtml = isDiscounted
      ? '<span class="rockpot-drawer__item-price-original">' + formatMoney(item.original_price) + '</span> '
        + '<span class="rockpot-drawer__item-price-sale">' + formatMoney(item.final_price) + '</span>'
      : '<span>' + formatMoney(item.final_price) + '</span>';

    // Line total (right-side total for the qty) — same treatment
    var isLineDiscounted = item.final_line_price < item.original_line_price;
    var linePriceHtml = isLineDiscounted
      ? '<span class="rockpot-drawer__item-line-price-original">' + formatMoney(item.original_line_price) + '</span> '
        + '<span class="rockpot-drawer__item-line-price-sale">' + formatMoney(item.final_line_price) + '</span>'
      : formatMoney(item.final_line_price);

    var key = escapeHtml(item.key);

    return [
      '<div class="rockpot-drawer__item" data-key="' + key + '">',
        '<a href="' + escapeHtml(item.url) + '" class="rockpot-drawer__item-image">' + image + '</a>',
        '<div class="rockpot-drawer__item-info">',
          '<a href="' + escapeHtml(item.url) + '" class="rockpot-drawer__item-name">' + escapeHtml(item.product_title) + '</a>',
          variantLine,
          '<div class="rockpot-drawer__item-price">' + priceHtml + '</div>',
          (item.properties && item.properties._rp_gift)
            ? '<div class="rockpot-drawer__item-qty rockpot-drawer__item-qty--gift">Free with bundle &middot; Qty <span data-qty-value>' + item.quantity + '</span></div>'
            : [
          '<div class="rockpot-drawer__item-qty">',
            '<button type="button" class="rockpot-drawer__qty-btn" data-qty-minus aria-label="Decrease quantity">&minus;</button>',
            '<span class="rockpot-drawer__qty-value" data-qty-value>' + item.quantity + '</span>',
            '<button type="button" class="rockpot-drawer__qty-btn" data-qty-plus aria-label="Increase quantity">+</button>',
          '</div>'].join(''),
        '</div>',
        '<div class="rockpot-drawer__item-actions">',
          '<div class="rockpot-drawer__item-line-price">' + linePriceHtml + '</div>',
          '<button type="button" class="rockpot-drawer__item-remove" data-remove>Remove</button>',
        '</div>',
      '</div>'
    ].join('');
  }

  // =========================================================
  // Upsell block
  // =========================================================

  var upsellCache = null;

  function loadUpsellProducts(upsellEl, handles, cart) {
    var itemsEl = $('[data-upsell-items]', upsellEl);
    if (!itemsEl) return;

    var cartHandles = (cart.items || []).map(function (i) { return i.handle; });
    var neededHandles = handles.filter(function (h) { return cartHandles.indexOf(h) === -1; });

    if (!neededHandles.length) {
      upsellEl.hidden = true;
      return;
    }

    if (upsellCache) {
      renderUpsells(itemsEl, upsellCache, neededHandles, upsellEl);
      return;
    }

    Promise.all(handles.map(function (handle) {
      return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (results) {
      upsellCache = results.filter(Boolean);
      renderUpsells(itemsEl, upsellCache, neededHandles, upsellEl);
    });
  }

  function renderUpsells(itemsEl, products, neededHandles, upsellEl) {
    var filtered = products.filter(function (p) { return neededHandles.indexOf(p.handle) !== -1; });
    if (!filtered.length) {
      itemsEl.innerHTML = '';
      if (upsellEl) upsellEl.hidden = true;
      return;
    }
    if (upsellEl) upsellEl.hidden = false;
    itemsEl.innerHTML = filtered.map(function (p) {
      var image = p.featured_image
        ? '<img src="' + escapeHtml(p.featured_image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
        : '';

      // Product-level availability: true if ANY variant has stock. The specific variant
      // we'd add is the first available one (or variants[0] as fallback for the ID).
      var isAvailable = p.available === true;
      var firstAvailableVariant = null;
      if (p.variants && p.variants.length) {
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].available) { firstAvailableVariant = p.variants[i]; break; }
        }
        if (!firstAvailableVariant) firstAvailableVariant = p.variants[0];
      }
      var variantId = firstAvailableVariant ? firstAvailableVariant.id : '';

      // Action button: +ADD when in stock, SOLD OUT (disabled) when not
      var actionHtml = isAvailable
        ? '<button type="button" class="rockpot-drawer__upsell-add" data-upsell-add data-variant-id="' + variantId + '">+ Add</button>'
        : '<span class="rockpot-drawer__upsell-sold-out" aria-label="Sold out">Sold Out</span>';

      return [
        '<div class="rockpot-drawer__upsell-item" data-handle="' + escapeHtml(p.handle) + '" data-sold-out="' + (isAvailable ? 'false' : 'true') + '">',
          '<div class="rockpot-drawer__upsell-image">' + image + '</div>',
          '<div class="rockpot-drawer__upsell-info">',
            '<div class="rockpot-drawer__upsell-name">' + escapeHtml(p.title) + '</div>',
            '<div class="rockpot-drawer__upsell-price">' + formatMoney(p.price) + '</div>',
          '</div>',
          actionHtml,
        '</div>'
      ].join('');
    }).join('');
  }

  // =========================================================
  // Refresh cart (fetch /cart.js, render, optionally open)
  // =========================================================

  // Free-gift sync: gift lines carry a hidden _rp_gift property holding the
  // bundle's product handle. Their quantity must always equal the total
  // quantity of that bundle in the cart (2 bundles = 2 of each gift; bundle
  // removed = gifts removed), so nobody pays full price for an orphaned gift
  // and nobody gets a second set of gifts without a second bundle.
  function syncGifts(cart) {
    if (!cart || !cart.items) return Promise.resolve(cart);
    var bundleQty = {};
    cart.items.forEach(function (it) {
      bundleQty[it.handle] = (bundleQty[it.handle] || 0) + it.quantity;
    });
    var updates = {}, needed = false;
    cart.items.forEach(function (it) {
      var b = it.properties && it.properties._rp_gift;
      if (!b) return;
      var want = bundleQty[b] || 0;
      if (it.quantity !== want) { updates[it.key] = want; needed = true; }
    });
    if (!needed) return Promise.resolve(cart);
    return nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.ok ? r.json() : cart; })
      .catch(function () { return cart; });
  }

  function refreshCart(openAfter, wasAfterAdd) {
    if (isRefreshing) return Promise.resolve();
    isRefreshing = true;

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(syncGifts)
      .then(function (cart) {
        updateBadge(cart.item_count);
        renderCart(cart);
        if (openAfter) openDrawer(wasAfterAdd);
        return cart;
      })
      .catch(function () {})
      .finally(function () {
        isRefreshing = false;
      });
  }

  // =========================================================
  // Quantity adjust / remove
  // =========================================================

  function changeItem(key, quantity) {
    var itemsEl = drawer ? $('[data-cart-items]', drawer) : null;
    if (itemsEl) itemsEl.setAttribute('data-loading', 'true');

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(syncGifts)
      .then(function (cart) {
        updateBadge(cart.item_count);
        renderCart(cart);
      })
      .catch(function () {})
      .finally(function () {
        if (itemsEl) itemsEl.removeAttribute('data-loading');
      });
  }

  function addVariantToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    })
      .then(function (r) { return r.json(); });
  }

  // =========================================================
  // Wire drawer events
  // =========================================================

  function bindDrawerEvents() {
    if (!ensureDrawer()) return;

    var closeBtn = $('[data-rockpot-drawer-close]', drawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });

    drawer.addEventListener('click', function (e) {
      var target = e.target;

      var itemEl = target.closest('.rockpot-drawer__item');
      if (itemEl) {
        var key = itemEl.getAttribute('data-key');
        var qtyValEl = $('[data-qty-value]', itemEl);
        var currentQty = qtyValEl ? parseInt(qtyValEl.textContent, 10) : 1;

        if (target.closest('[data-qty-plus]')) {
          changeItem(key, currentQty + 1);
          return;
        }
        if (target.closest('[data-qty-minus]')) {
          changeItem(key, Math.max(0, currentQty - 1));
          return;
        }
        if (target.closest('[data-remove]')) {
          changeItem(key, 0);
          return;
        }
      }

      var upsellBtn = target.closest('[data-upsell-add]');
      if (upsellBtn) {
        var variantId = upsellBtn.getAttribute('data-variant-id');
        if (!variantId) return;
        upsellBtn.disabled = true;
        upsellBtn.textContent = 'Adding...';
        addVariantToCart(variantId, 1)
          .then(function () { return refreshCart(false, false); })
          .finally(function () {
            upsellBtn.disabled = false;
            upsellBtn.textContent = '+ Add';
          });
      }
    });

    $$(CART_LINK_SELECTOR).forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        refreshCart(true, false); // open drawer — this was NOT an ATC event
      });
    });
  }

  // =========================================================
  // Intercept ATC requests (fetch, XHR, form submit)
  // =========================================================

  // Helper: classify a cart URL. We want to sync on any cart mutation, but only
  // auto-OPEN the drawer on /cart/add (not on change/update/clear — those usually
  // happen on the cart page itself where opening the drawer would be jarring).
  function cartEventKind(url) {
    if (!url) return null;
    if (url.indexOf('/cart/add') !== -1) return 'add';
    if (url.indexOf('/cart/change') !== -1) return 'change';
    if (url.indexOf('/cart/update') !== -1) return 'update';
    if (url.indexOf('/cart/clear') !== -1) return 'clear';
    return null;
  }

  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url ? args[0].url : '');
    var kind = cartEventKind(url);

    var result = originalFetch.apply(this, args);

    if (kind) {
      result.then(function () {
        setTimeout(function () {
          // Open drawer only on ADD; other mutations just sync badge + drawer contents silently
          var shouldOpen = kind === 'add';
          refreshCart(shouldOpen, shouldOpen);
        }, 50);
      }).catch(function () {});
    }

    return result;
  };

  var originalXHROpen = XMLHttpRequest.prototype.open;
  var originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._rockpotURL = url;
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    var url = xhr._rockpotURL || '';
    var kind = cartEventKind(url);

    if (kind) {
      xhr.addEventListener('load', function () {
        setTimeout(function () {
          var shouldOpen = kind === 'add';
          refreshCart(shouldOpen, shouldOpen);
        }, 50);
      });
    }

    return originalXHRSend.apply(this, arguments);
  };

  // Form submit fallback — ONLY runs on themes that don't already handle ATC via AJAX.
  // On Motion, theme.js intercepts the submit and calls preventDefault + its own fetch.
  // Our fetch/XHR interceptors catch Motion's fetch and sync the drawer.
  // This handler is a fallback for non-AJAX themes only.
  document.addEventListener('submit', function (e) {
    // If something else already handled this submit, do nothing.
    // Critical: prevents double-adds when Motion's theme.js fires first.
    if (e.defaultPrevented) return;

    var form = e.target;
    if (!form || !form.action) return;
    if (form.action.indexOf('/cart/add') === -1) return;

    // Free-gifts app handles its own ATC flow
    if (document.querySelectorAll('[data-free-gift-variant]').length > 0) return;

    e.preventDefault();
    var formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(true, true); })
      .catch(function () { form.submit(); });
  });

  // =========================================================
  // Init
  // =========================================================

  function init() {
    bindDrawerEvents();
    refreshCart(false, false);

    // Defensive re-sync: catch any cart mutation we might have missed
    // (page came back from bfcache, user switched tabs and back, etc.)
    window.addEventListener('pageshow', function () {
      refreshCart(false, false);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        refreshCart(false, false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
